import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from '../../offices/modals/modal.styles';
import Facility from '../models/facility';
import LinkedOfficesGroup from '../linked-offices/groups/linked-office-group.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import type FacilityType from '../../facility-types/models/facility-type';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import FacilityTypesGroup from '../../facility-types/groups/facility-type-group.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly handleCreate: (value: Facility) => Promise<void>;
  readonly isFacilityValid: (value: Facility) => boolean;
}

const CreateFacilityModal = ({ showModal, handleClose, handleCreate, isFacilityValid }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [facility, setFacility] = useState<Facility>(new Facility());
  const [isSaving, setIsSaving] = useState(false);

  const resetModalState = (): void => {
    setFacility(new Facility());
  };

  const handleCloseModal = (): void => {
    resetModalState();
    handleClose();
  };

  const handleSave = async (): Promise<void> => {
    const facilityCopy = { ...facility };
    if (isFacilityValid(facilityCopy)) {
      setIsSaving(true);
      resetModalState();
      await handleCreate(facilityCopy);
      setIsSaving(false);
    }
  };

  const HandleFacilityNameChange = (value: string): void => {
    const facilityCopy = { ...facility };
    facilityCopy.name = value;
    setFacility(facilityCopy);
  };

  const handleFacilityTypeSelectionChanged = (selectedtype: FacilityType): void => {
    const facilityCopy = { ...facility };
    facilityCopy.type = selectedtype.facilityType;
    setFacility(facilityCopy);
  };

  const handleLinkedOfficesChanged = (newLinkedOffices: string[]): void => {
    const facilityCopy = { ...facility };
    facilityCopy.officeIds = [...newLinkedOffices];
    setFacility(facilityCopy);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCloseModal} style={styles.modalStyle(isMobile, 800)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>{t('create-new-facility')}</span>
        <LabeledInput
          labelText={t('name').toString()}
          value={facility.name}
          onChange={(name): void => {
            HandleFacilityNameChange(name);
          }}
          onEnter={(): void => {
            handleSave();
          }}
        />
        <FacilityTypesGroup
          initiallySelectedFacilityType={facility.type}
          handleFacilityTypeSelectionChanged={handleFacilityTypeSelectionChanged}
        />
        <LinkedOfficesGroup
          initiallySelectedLinkedOfficeIds={facility.officeIds}
          handleLinkedOfficeSelectionChanged={handleLinkedOfficesChanged}
        />
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              loading={isSaving}
              fullWidth
              onClick={(): void => {
                handleSave();
              }}
            >
              {t('create')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCloseModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default CreateFacilityModal;
